import { FC, ReactElement, useCallback } from 'react';
import classNames from 'classnames';
import { notify, NotificationType } from 'xstate/actors/NotificationActor';
import { Collection as CollectionModel } from 'models/Collection';
import useDeleteCollection from 'networking/mindfeed/hooks/useDeleteCollection';
import { openCollectionModal } from 'xstate/actors/ModalActor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faEllipsis } from '@fortawesome/pro-solid-svg-icons';
import {
  faCalendarClock,
  faClock,
  faFileLines,
  faPen,
  faPhotoFilm,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useTranslations } from 'next-intl';
import CollectionIcon from 'assets/icons/icon-collection.svg';
import ActionMenu, { ActionMenuEntry } from 'elements/ActionMenu';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';
import { useSpaceNavigation } from 'xstate/hooks/useSpaceNavigation';
import { ActionableModalPayload } from 'xstate/machines/ModalMachine';
import { openActionableModal } from 'xstate/actors/ModalActor';
import type { BoxView } from 'models/BoxView';

// The icon of each built-in view, shared with the box header and the
// compact switcher.
export const BOX_VIEW_ICONS: Record<BoxView, IconDefinition> = {
  timeline: faClock,
  journal: faCalendarClock,
  library: faPhotoFilm,
  files: faFileLines,
};

interface BoxViewCardProps {
  view: BoxView;
  isSelected?: boolean;
  onSelect?: () => void;
  // `wide`: the icon beside the name and its description, across the whole
  // column (the Timeline). `compact`: the icon over the name, three abreast
  // (Journal, Library, Files); the description becomes a tooltip. `row`: a
  // plain row like a collection's, for the narrow switcher panel.
  layout: 'wide' | 'compact' | 'row';
}

// A built-in view as a small card: a hairline card at rest, the rows'
// selection tint when chosen.
const BoxViewCard: FC<BoxViewCardProps> = ({
  view,
  layout,
  isSelected = false,
  onSelect,
}): ReactElement => {
  const t = useTranslations('collections.views');
  const { goToView } = useSpaceNavigation();

  const onClick = useCallback(() => {
    goToView(view);
    onSelect?.();
  }, [goToView, view, onSelect]);

  const isWide = layout === 'wide';

  if (layout === 'row') {
    return (
      <div
        className={styles.row(isSelected)}
        onClick={onClick}
        title={t(`${view}.subtitle`)}
      >
        <span className={styles.icon.wrap} aria-hidden="true">
          <FontAwesomeIcon
            icon={BOX_VIEW_ICONS[view]}
            className={styles.icon.view}
          />
        </span>
        <span className={styles.text}>
          <span className={styles.title}>{t(`${view}.title`)}</span>
        </span>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        styles.card.container(isSelected),
        isWide ? styles.card.wide : styles.card.compact,
      )}
      onClick={onClick}
      title={isWide ? undefined : t(`${view}.subtitle`)}
    >
      <FontAwesomeIcon
        icon={BOX_VIEW_ICONS[view]}
        className={styles.card.icon}
        aria-hidden="true"
      />
      <span className={styles.card.text}>
        <span className={styles.card.title}>{t(`${view}.title`)}</span>
        {isWide && (
          <span className={styles.card.subtitle}>{t(`${view}.subtitle`)}</span>
        )}
      </span>
    </div>
  );
};

interface CollectionProps {
  collection: CollectionModel;
  isSelected?: boolean;
  onSelect?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const Collection: FC<CollectionProps> = ({
  collection,
  isSelected = false,
  onSelect,
  onEdit,
  onDelete,
}): ReactElement => {
  const t = useTranslations();
  const { selectedCollection } = useActiveSpace();
  const { goToCollection, goToView } = useSpaceNavigation();

  const deleteCollectionMutation = useDeleteCollection({
    onSuccess: () => {
      // Notify User when collection is deleted
      notify(NotificationType.COLLECTION_DELETED);

      // If we were viewing the deleted collection, fall back to the box's
      // Timeline (resets the selection and navigates off the now-dead URL).
      if (selectedCollection?.id === collection.id) {
        goToView('timeline');
      }
    },
    removeOnSuccess: false,
  });

  const openDeleteModal = () => {
    const bodyElement = (
      <div className="my-5">
        <span className="block my-4">{t('collections.delete.message')}</span>
        <div className="bg-blue-100 rounded-lg p-3 mt-4 flex flex-row justify-center items-start space-x-3">
          <FontAwesomeIcon icon={faCircleInfo} className="text-blue-600 pt-1" />
          <span className="mb-2">{t('collections.delete.memosKept')}</span>
        </div>
      </div>
    );

    const payload: ActionableModalPayload = {
      title: t('collections.delete.title', { name: collection.name }),
      bodyElement,
      onAccept: () => deleteCollectionMutation.mutate(collection.id),
      acceptLabel: t('common.delete'),
    };
    openActionableModal(payload);
  };

  const onContainerClick = useCallback(() => {
    goToCollection(collection);
    onSelect?.();
  }, [goToCollection, collection, onSelect]);

  const handleEdit = () => {
    openCollectionModal(collection);
    onEdit?.();
  };

  const handleDelete = () => {
    openDeleteModal();
    onDelete?.();
  };

  const menuItems: ActionMenuEntry[] = [
    { icon: faPen, label: t('common.edit'), onClick: handleEdit },
    'separator',
    {
      icon: faTrash,
      label: t('common.delete'),
      danger: true,
      onClick: handleDelete,
    },
  ];

  return (
    // We use onClick instead of a <Link /> attribute to prevent clicks on
    // MenuButton from triggering the parent link
    <div onClick={onContainerClick}>
      <div key={collection.id} className={styles.row(isSelected)}>
        <span className={styles.icon.wrap} aria-hidden="true">
          {collection.emoji ? (
            <span className={styles.icon.emoji}>{collection.emoji}</span>
          ) : (
            <CollectionIcon className={styles.icon.glyph} />
          )}
        </span>
        <span className={styles.text}>
          <span className={styles.title}>{collection.name}</span>
        </span>
        <ActionMenu
          className={styles.right.container}
          triggerClassName={styles.right.btn.container(isSelected)}
          triggerAriaLabel={t('collections.options')}
          stopPropagation
          anchor="bottom end"
          size="md"
          items={menuItems}
          trigger={() => (
            <FontAwesomeIcon
              icon={faEllipsis}
              className={styles.right.btn.icon}
            />
          )}
        />
      </div>
    </div>
  );
};

const styles = {
  row: (isSelected: boolean) =>
    classNames(
      'group flex items-center gap-3.5 w-full rounded-[10px] border px-3 py-1.5',
      'cursor-pointer select-none',
      isSelected
        ? 'border-surface-selection-border bg-surface-selection'
        : 'border-transparent hover:bg-ink-secondary/8',
    ),
  icon: {
    wrap: 'flex-none flex items-center justify-center w-7 h-7 text-ink-secondary',
    view: 'fa-fw text-[15px]',
    emoji: 'text-[22px] leading-none',
    glyph: 'w-5 h-5',
  },
  // The view cards. Padding kept tight so they sit close to the rows below.
  card: {
    container: (isSelected: boolean) =>
      classNames(
        'flex min-w-0 rounded-xl border px-3 py-1.5 cursor-pointer select-none',
        isSelected
          ? 'border-surface-selection-border bg-surface-selection'
          : 'border-hairline bg-surface-card hover:bg-ink-secondary/8',
      ),
    wide: 'items-center gap-3',
    compact: 'flex-col gap-0.5',
    icon: 'fa-fw text-[17px] text-ink-secondary',
    text: 'flex min-w-0 flex-col',
    title: 'truncate text-[13px] font-semibold leading-5 text-ink-primary',
    subtitle: 'truncate text-[11px] leading-4 text-ink-secondary',
  },
  text: 'flex-1 min-w-0',
  title: 'block text-sm font-semibold leading-5 text-ink-primary truncate',
  right: {
    container: 'relative',
    btn: {
      container: (isSelected: boolean) =>
        classNames(
          'inline-flex items-center rounded-md px-1 py-1.5 text-ink-secondary',
          'hover:bg-ink-secondary/10 focus:outline-hidden data-hover:bg-ink-secondary/10 data-open:bg-ink-secondary/10',
          isSelected && 'visible lg:invisible lg:group-hover:visible',
          !isSelected && 'invisible lg:group-hover:visible',
        ),
      icon: 'fa-fw text-xs',
    },
  },
};

export { Collection, BoxViewCard };
